import { RefObject, useEffect, useRef, useState } from 'react';

interface UseInlineLogoHaloExtensionArgs {
  /**
   * Whether the inline logo is rendered and the caller wants its
   * scaled extension measured. The hook short-circuits to 0 (and
   * detaches the observer) when false, so non-admin renders can keep
   * this off entirely.
   */
  enabled: boolean;
  /** Current logo scale factor (e.g. 1.5 for `logoPercentScale: 150`). */
  scaleFactor: number;
}

interface UseInlineLogoHaloExtensionResult {
  /**
   * Ref the caller attaches to the inline logo wrapper element. The
   * hook observes its `offsetHeight` to compute the scaled extension.
   */
  ref: RefObject<HTMLDivElement | null>;
  /**
   * Pixel amount the wrapper's CSS transform extends the visible
   * image above its layout box top (i.e. how far the admin's hover
   * halo target needs to grow upward to wrap the scaled visual as
   * part of a single rectangle).
   */
  extensionPx: number;
}

/**
 * Shared measurement for the admin branding preview's "single halo
 * over the logo + title" indicator. When the inline logo is scaled up,
 * `transform: scale` extends the visible image above its layout box
 * without affecting layout — so the title-halo target's natural box
 * doesn't include the scaled visual. This hook computes that
 * extension at runtime (via `ResizeObserver`) so the caller can pad
 * the halo target up by exactly the right amount.
 *
 * Used by `CoverSlide` and `DividerSlide`; their layouts differ only
 * in whether a `HeaderComponent` override is in play, which the
 * caller folds into `enabled`.
 */
export function useInlineLogoHaloExtension({
  enabled,
  scaleFactor,
}: UseInlineLogoHaloExtensionArgs): UseInlineLogoHaloExtensionResult {
  const ref = useRef<HTMLDivElement | null>(null);
  const [extensionPx, setExtensionPx] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || scaleFactor <= 1) {
      setExtensionPx(0);
      return;
    }
    const measure = () => {
      setExtensionPx(el.offsetHeight * (scaleFactor - 1));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, scaleFactor]);

  return { ref, extensionPx };
}
